// src/context/WishlistContext.jsx - HYBRID APPROACH (Firestore + localStorage)
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  isUserLoggedIn,
  loadWishlistFromFirestore,
  addToWishlistFirestore,
  removeFromWishlistFirestore,
  toggleWishlistFirestore,
  clearWishlistFirestore,
  migrateLocalStorageToFirestore
} from '../services/userDataService';

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFirestore, setUseFirestore] = useState(false);

  // İlk yüklemede favorileri yükle
  useEffect(() => {
    loadInitialWishlist();
  }, []);

  const loadInitialWishlist = async () => {
    setLoading(true);

    if (isUserLoggedIn()) {
      // Kullanıcı giriş yapmış - Firestore kullan
      console.log('🔥 Firestore kullanılıyor (giriş yapılmış) - Wishlist');
      setUseFirestore(true);

      // Önce migration yap
      const migrationResult = await migrateLocalStorageToFirestore();
      if (migrationResult.success && migrationResult.migrated) {
        console.log('✅ Migration yapıldı, Firestore\'dan yükleniyor - Wishlist');
        setWishlist(migrationResult.wishlist || []);
        setLoading(false);
        return;
      }

      // Migration gereksizse direkt yükle
      const result = await loadWishlistFromFirestore();
      if (result.success) {
        setWishlist(result.wishlist || []);
      } else {
        // Firestore başarısız olursa localStorage'a geç
        console.warn('⚠️ Firestore yüklenemedi, localStorage kullanılıyor - Wishlist');
        loadFromLocalStorage();
      }
    } else {
      // Kullanıcı giriş yapmamış - localStorage kullan
      console.log('💾 localStorage kullanılıyor (giriş yapılmamış) - Wishlist');
      setUseFirestore(false);
      loadFromLocalStorage();
    }

    setLoading(false);
  };

  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('trona_wishlist');
      if (saved) {
        const parsed = JSON.parse(saved);
        setWishlist(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('localStorage yüklenemedi - Wishlist:', error);
      setWishlist([]);
    }
  };

  // localStorage'a kaydetme (sadece giriş yapılmamışsa)
  useEffect(() => {
    if (!loading && !useFirestore) {
      try {
        localStorage.setItem('trona_wishlist', JSON.stringify(wishlist));
      } catch (error) {
        console.error('localStorage kaydedilemedi - Wishlist:', error);
      }
    }
  }, [wishlist, loading, useFirestore]);

  // Favorilere ekle/çıkar (toggle)
  const toggleWishlist = async (product) => {
    if (!product || !product.id) {
      console.error('Geçersiz ürün');
      return;
    }

    if (useFirestore) {
      // Firestore'da toggle
      const result = await toggleWishlistFirestore(product);
      if (result.success) {
        setWishlist(result.wishlist);
      } else if (result.useLocalStorage) {
        // Firestore başarısız, localStorage kullan
        toggleLocalStorage(product);
      }
    } else {
      // localStorage'da toggle
      toggleLocalStorage(product);
    }
  };

  const toggleLocalStorage = (product) => {
    setWishlist(prev => {
      const exists = prev.find(item => item.id === product.id);
      if (exists) {
        return prev.filter(item => item.id !== product.id);
      }
      return [...prev, {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        discount: product.discount || 0,
        category: product.category,
        description: product.description,
        rating: product.rating,
        addedAt: new Date().toISOString()
      }];
    });
  };

  // Favorilere ekle
  const addToWishlist = async (product) => {
    if (!product || !product.id) return;

    if (useFirestore) {
      const result = await addToWishlistFirestore(product);
      if (result.success) {
        setWishlist(result.wishlist);
      } else if (result.useLocalStorage) {
        addToLocalStorage(product);
      }
    } else {
      addToLocalStorage(product);
    }
  };

  const addToLocalStorage = (product) => {
    setWishlist(prev => {
      const exists = prev.find(item => item.id === product.id);
      if (exists) return prev;
      
      return [...prev, {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        discount: product.discount || 0,
        category: product.category,
        description: product.description,
        rating: product.rating,
        addedAt: new Date().toISOString()
      }];
    });
  };

  // Favorilerden çıkar
  const removeFromWishlist = async (productId) => {
    if (useFirestore) {
      const result = await removeFromWishlistFirestore(productId);
      if (result.success) {
        setWishlist(result.wishlist);
      } else if (result.useLocalStorage) {
        setWishlist(prev => prev.filter(item => item.id !== productId));
      }
    } else {
      setWishlist(prev => prev.filter(item => item.id !== productId));
    }
  };

  // Favorilerde mi kontrol et
  const isInWishlist = (productId) => {
    return wishlist.some(item => item.id === productId);
  };

  // Tüm favorileri temizle
  const clearWishlist = async () => {
    if (useFirestore) {
      const result = await clearWishlistFirestore();
      if (result.success || result.useLocalStorage) {
        setWishlist([]);
      }
    } else {
      setWishlist([]);
    }
  };

  // Favori ürünü getir
  const getWishlistItem = (productId) => {
    return wishlist.find(item => item.id === productId) || null;
  };

  // Kullanıcı giriş yaptığında context'i yeniden yükle
  const refreshWishlist = async () => {
    await loadInitialWishlist();
  };

  return (
    <WishlistContext.Provider value={{
      wishlist,
      wishlistCount: wishlist.length,
      loading,
      useFirestore,
      toggleWishlist,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      clearWishlist,
      getWishlistItem,
      refreshWishlist // Yeni: Giriş yapınca çağrılabilir
    }}>
      {children}
    </WishlistContext.Provider>
  );
}

// Custom hook
export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}

export default WishlistContext;